(() => {
  const initializedSections = new WeakSet();
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const normalize = (value) => value.trim().toLocaleLowerCase();
  const splitValues = (value) =>
    String(value || '')
      .split(/[|,]/)
      .map((item) => item.trim())
      .filter(Boolean);

  const initSection = (section) => {
    if (initializedSections.has(section)) return;
    initializedSections.add(section);

    const catalog = section.querySelector('[data-product-catalog]');
    const filtersBar = section.querySelector('.new-design-featured-products__filters');
    const cards = Array.from(section.querySelectorAll('[data-product-card]'));
    const viewButtons = Array.from(section.querySelectorAll('[data-view-button]'));
    const filterButtons = Array.from(section.querySelectorAll('[data-filter-button]'));
    const emptyState = section.querySelector('[data-empty-state]');
    const sheet = section.querySelector('[data-filter-sheet]');
    const panel = sheet?.querySelector('[data-sheet-panel]');
    const title = sheet?.querySelector('[data-sheet-title]');
    const options = sheet?.querySelector('[data-sheet-options]');
    const reset = sheet?.querySelector('[data-sheet-reset]');
    const handle = sheet?.querySelector('[data-sheet-handle]');
    const grabZone = sheet?.querySelector('[data-sheet-grab]');

    if (!catalog || !cards.length || !sheet || !panel || !title || !options || !reset) return;

    const state = {
      view: 'list',
      sort: 'best-selling',
      category: '',
      activeType: '',
      activeButton: null,
      closeTimer: 0,
      dragStartY: 0,
      dragOffset: 0,
      dragging: false,
    };

    const labels = {
      sort: ((window.sifStrings?.cards?.sortBy) || 'Sort by'),
      category: ((window.sifStrings?.cards?.foodCategory) || 'Food category'),
    };

    const allLabels = {
      category: ((window.sifStrings?.cards?.allCategories) || 'All categories'),
    };

    const sortOptions = [
      ['best-selling', ((window.sifStrings?.cards?.bestSelling) || 'Best selling')],
      ['new', ((window.sifStrings?.cards?.sortNew) || 'New')],
      ['price-ascending', ((window.sifStrings?.cards?.priceLowHigh) || 'Price low–high')],
      ['price-descending', ((window.sifStrings?.cards?.priceHighLow) || 'Price high–low')],
    ];

    const isDesktop = () => !mobileQuery.matches;

    const galleries = [];

    const refreshGalleries = () => {
      galleries.forEach((embla) => embla.reInit());
    };

    // Once an image has actually loaded, stop advertising it as lazy, so no
    // later layout change can talk the browser into dropping and re-fetching
    // it. Defensive: the overflow-based scroll lock doesn't move anything by
    // itself, but sort/filter reorders still detach and re-insert cards.
    // Images still in flight get pinned by their own load handler (at init).
    const settleLazyImages = () => {
      section.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        if (img.complete && img.naturalWidth > 0) img.loading = 'eager';
      });
    };

    const initGalleries = (attempt = 0) => {
      if (typeof window.EmblaCarousel === 'undefined') {
        if (attempt < 40) window.setTimeout(() => initGalleries(attempt + 1), 100);
        return;
      }

      // Build a carousel only once its card is near the viewport.
      //
      // The homepage stacks several of these sections and ends up with ~67
      // cards; constructing every Embla instance up front was 1.6s of script
      // and 16 long tasks, nearly all of it for carousels the reader never
      // scrolls to. The observer defers that cost to the moment it is about
      // to matter — 600px ahead, so the work still finishes before the card
      // is actually looked at.
      //
      // Without IntersectionObserver everything initialises immediately, as
      // before: a slower first load beats a gallery that cannot be swiped.
      const galleryElements = Array.from(section.querySelectorAll('[data-product-gallery]'));

      if ('IntersectionObserver' in window) {
        const galleryObserver = new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              observer.unobserve(entry.target);
              buildGallery(entry.target);
            });
          },
          { rootMargin: '600px 0px' }
        );
        galleryElements.forEach((el) => galleryObserver.observe(el));
      } else {
        galleryElements.forEach(buildGallery);
      }
    };

    function buildGallery(galleryElement) {
      {
        if (galleryElement.dataset.galleryInitialized === 'true') return;

        const track = galleryElement.querySelector('[data-embla-container]');
        const slideCount = track ? track.children.length : 0;
        if (!track || slideCount === 0) return;

        galleryElement.dataset.galleryInitialized = 'true';

        // Single image → no carousel needed; skip so it stays static.
        if (slideCount < 2) return;
        galleryElement.classList.add('is-embla');

        const embla = window.EmblaCarousel(galleryElement, {
          loop: false,
          align: 'start',
          containScroll: 'trimSnaps',
          dragFree: false,
          // Snappier arrow/dot transitions (Embla: higher = slower, range 20–60).
          duration: 20,
        });

        galleries.push(embla);

        // Preload the neighbours of the selected slide. Lazy images that sit
        // off-viewport inside the carousel don't load until they intersect, so
        // without this a swipe lands on a still-loading (blank) image.
        const slides = embla.slideNodes();

        // Slides past the second ship as 1x1 placeholders carrying their real
        // URL in data-src (see the card snippet for why). Promote one to a
        // real image the first time it comes within reach of the viewport.
        const upgradeSlide = (slide) => {
          slide?.querySelectorAll('img[data-src]').forEach((img) => {
            // srcset before src: setting src first makes the browser start
            // fetching that URL, and it may keep it even once a srcset arrives.
            if (img.dataset.srcset) img.srcset = img.dataset.srcset;
            if (img.dataset.sizes) img.sizes = img.dataset.sizes;
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            img.removeAttribute('data-srcset');
            img.removeAttribute('data-sizes');
          });
        };

        const preloadAround = () => {
          const selected = embla.selectedScrollSnap();
          [selected, selected + 1, selected - 1].forEach((i) => {
            const slide = slides[i];
            if (!slide) return;
            upgradeSlide(slide);
            slide.querySelectorAll('img[loading="lazy"]').forEach((img) => {
              img.loading = 'eager';
            });
          });
        };
        embla.on('select', preloadAround);
        preloadAround();

        const media = galleryElement.closest('.new-design-featured-products__media');

        // Desktop hover arrows.
        const prev = media?.querySelector('[data-gallery-prev]');
        const next = media?.querySelector('[data-gallery-next]');
        prev?.addEventListener('click', (event) => {
          event.preventDefault();
          embla.scrollPrev();
          event.currentTarget.blur();
        });
        next?.addEventListener('click', (event) => {
          event.preventDefault();
          embla.scrollNext();
          event.currentTarget.blur();
        });

        // Hide the arrow at each edge (loop is off — nothing to scroll to).
        const syncArrows = () => {
          prev?.classList.toggle('is-hidden', !embla.canScrollPrev());
          next?.classList.toggle('is-hidden', !embla.canScrollNext());
        };
        embla.on('select', syncArrows);
        embla.on('reInit', syncArrows);
        syncArrows();

        // Page dots — iOS-style sliding window. When there are more dots than
        // the window (WINDOW), all dots still render (each clickable), but the
        // whole row is translated so the active dot stays inside a fixed 5-slot
        // window, and dots are sized by distance from the active one: active +
        // its immediate neighbours full-size, the window's edge dots shrunk to
        // hint "more this way", everything past the window hidden. Under the
        // window count it's just plain dots. Size/offset are pure CSS via a
        // per-dot data-attr + a CSS var, so this only sets state, never layout.
        const dotsWrap = media?.querySelector('[data-gallery-dots]');
        const DOT_STEP = 12; // px between dot centres — must match the CSS
        const WINDOW = 5;
        let dots = [];
        if (dotsWrap) {
          const buildDots = () => {
            dotsWrap.replaceChildren();
            dots = embla.scrollSnapList().map((_, index) => {
              const dot = document.createElement('span');
              dot.className = 'new-design-featured-products__dot';
              dot.addEventListener('click', () => embla.scrollTo(index));
              dotsWrap.appendChild(dot);
              return dot;
            });
            dotsWrap.classList.toggle(
              'new-design-featured-products__dots--windowed',
              dots.length > WINDOW
            );
          };

          const syncDots = () => {
            const count = dots.length;
            const selected = embla.selectedScrollSnap();

            if (count <= WINDOW) {
              dots.forEach((dot, i) => {
                dot.classList.toggle('is-selected', i === selected);
                dot.removeAttribute('data-rank');
              });
              dotsWrap.style.removeProperty('--dots-shift');
              return;
            }

            // Clamp the window's centre so it can't scroll past either end:
            // the active dot rides the centre in the middle, and sits toward
            // the window edge near the first/last two dots.
            const half = Math.floor(WINDOW / 2); // 2
            const centre = Math.min(Math.max(selected, half), count - 1 - half);
            dotsWrap.style.setProperty('--dots-shift', `${-(centre - half) * DOT_STEP}px`);

            dots.forEach((dot, i) => {
              dot.classList.toggle('is-selected', i === selected);
              const dist = Math.abs(i - centre); // distance from window centre
              // 0 = active-ish centre (full), 1 = full, 2 = edge (small),
              // >2 = outside the window (hidden). Drives size in CSS.
              dot.dataset.rank = dist > half ? 'out' : String(dist);
            });
          };

          buildDots();
          embla.on('select', syncDots);
          embla.on('reInit', () => {
            if (embla.scrollSnapList().length !== dots.length) buildDots();
            syncDots();
          });
          syncDots();
        }
      }
    }

    // Parsed once per type and cached: valuesFor runs on every filter change,
    // and re-splitting the same string each time is pure waste.
    const orderCache = new Map();
    const categoryOrder = (type) => {
      if (orderCache.has(type)) return orderCache.get(type);
      // Only the category filter has a configured order; any other type gets an
      // empty list and falls through to alphabetical.
      const raw = type === 'category' ? section.dataset.categoryOrder || '' : '';
      const order = raw.split(',').map(normalize).filter(Boolean);
      orderCache.set(type, order);
      return order;
    };

    const valuesFor = (type) => {
      const counts = new Map();

      cards.forEach((card) => {
        splitValues(card.dataset[type]).forEach((value) => {
          const key = normalize(value);
          if (!key) return;
          const current = counts.get(key);
          counts.set(key, {
            label: current?.label || value,
            count: (current?.count || 0) + 1,
          });
        });
      });

      // Merchant-defined order, falling back to alphabetical.
      //
      // The options are built by walking the cards, so their natural order is
      // whatever the catalogue happens to be — and a metafield definition's own
      // choice order is not exposed to the theme by Shopify, so it cannot be
      // read from there either. The section setting repeats that order, and
      // anything absent from it sorts alphabetically after the listed values,
      // which keeps a newly added category visible rather than silently first.
      const order = categoryOrder(type);
      return Array.from(counts.entries())
        .map(([value, details]) => ({ value, ...details }))
        .sort((a, b) => {
          const ia = order.indexOf(normalize(a.label));
          const ib = order.indexOf(normalize(b.label));
          if (ia !== -1 && ib !== -1) return ia - ib;
          if (ia !== -1) return -1;
          if (ib !== -1) return 1;
          return a.label.localeCompare(b.label);
        });
    };

    const buttonFor = (type) => filterButtons.find((button) => button.dataset.filterButton === type);

    const defaultValueFor = (type) => (type === 'sort' ? 'best-selling' : '');

    // Reset button is "active" (dark) only when the current sheet's filter has
    // a non-default value to clear; muted otherwise.
    const syncReset = () => {
      const type = state.activeType;
      const hasValue = type ? state[type] !== defaultValueFor(type) : false;
      reset.classList.toggle('is-active', hasValue);
      reset.disabled = !hasValue;
    };

    const clearFilter = (type) => {
      state[type] = defaultValueFor(type);
      applyState();
      if (state.activeType === type) {
        renderOptions(type);
      }
    };

    const updateFilterButton = (type) => {
      const button = buttonFor(type);
      if (!button) return;
      const label = button.querySelector('[data-filter-label]');
      const selectedValue = state[type];
      const defaultLabel = button.dataset.defaultLabel || type;

      let selectedLabel = defaultLabel;
      if (type === 'sort') {
        selectedLabel = sortOptions.find(([value]) => value === selectedValue)?.[1] || defaultLabel;
      } else {
        selectedLabel = valuesFor(type).find(({ value }) => value === selectedValue)?.label || defaultLabel;
      }

      if (label) label.textContent = selectedLabel;

      const isFiltered = type === 'sort' ? selectedValue !== 'best-selling' : Boolean(selectedValue);
      button.classList.toggle('is-filtered', isFiltered);
      // Any active chip (sort or value filter) becomes a black "active" chip.
      // Sort has no ✕ affordance in its markup, so it just goes black.
      button.classList.toggle('is-active-filter', isFiltered);
    };

    const matchesFilter = (card, type) => {
      if (!state[type]) return true;
      return splitValues(card.dataset[type]).some((value) => normalize(value) === state[type]);
    };

    const applyState = () => {
      const sortedCards = [...cards].sort((a, b) => {
        if (state.sort === 'new') return Number(b.dataset.createdAt) - Number(a.dataset.createdAt);
        if (state.sort === 'price-ascending') return Number(a.dataset.price) - Number(b.dataset.price);
        if (state.sort === 'price-descending') return Number(b.dataset.price) - Number(a.dataset.price);
        return Number(a.dataset.originalOrder) - Number(b.dataset.originalOrder);
      });

      // Only touch the DOM if the order actually changed. appendChild() on an
      // already-connected node MOVES it — detach + re-insert — which drops the
      // decoded image and makes every card visibly repaint. Re-running this on
      // an unchanged order (e.g. opening/closing the filter sheet, which calls
      // applyState via clearFilter/change) is what made the product images
      // flicker. Comparing against the live children first makes the common
      // "nothing moved" case a no-op.
      const currentOrder = Array.from(catalog.children);
      const orderChanged =
        currentOrder.length !== sortedCards.length ||
        sortedCards.some((card, index) => currentOrder[index] !== card);

      if (orderChanged) {
        // Build the new order off-document so the browser does one insertion
        // instead of N individual moves.
        const fragment = document.createDocumentFragment();
        sortedCards.forEach((card) => fragment.appendChild(card));
        catalog.appendChild(fragment);
      }

      let visibleCount = 0;
      let visibilityChanged = false;
      cards.forEach((card) => {
        const visible = matchesFilter(card, 'category');
        // Writing card.hidden unconditionally is itself a style invalidation;
        // only assign when the value really flips. (card.hidden === visible
        // means the two disagree, since hidden is the negation of visible.)
        if (card.hidden === visible) {
          visibilityChanged = true;
          card.hidden = !visible;
        }
        // Set separately: cards render with no aria-hidden at all, so the
        // first pass has to stamp every card even though nothing "flipped".
        const ariaHidden = String(!visible);
        if (card.getAttribute('aria-hidden') !== ariaHidden) {
          card.setAttribute('aria-hidden', ariaHidden);
        }
        if (visible) visibleCount += 1;
      });

      ['sort', 'category'].forEach(updateFilterButton);
      emptyState?.classList.toggle('is-visible', visibleCount === 0);

      // Embla only needs a re-measure when cards actually moved or changed
      // visibility. reInit() tears the carousel down and rebuilds it, so
      // calling it unconditionally repainted every gallery on each open/close
      // of the filter sheet.
      if (orderChanged || visibilityChanged) {
        window.requestAnimationFrame(refreshGalleries);
      }
    };

    const setView = (view) => {
      state.view = view;
      section.dataset.view = view;
      viewButtons.forEach((button) => {
        const active = button.dataset.viewButton === view;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      window.requestAnimationFrame(refreshGalleries);
    };

    const positionPopover = () => {
      if (!isDesktop() || !state.activeButton) return;
      const buttonRect = state.activeButton.getBoundingClientRect();
      const sectionRect = section.getBoundingClientRect();
      const top = buttonRect.bottom - sectionRect.top + 8;
      let left = buttonRect.left - sectionRect.left;

      // Keep the popover inside the section horizontally.
      const panelWidth = panel.offsetWidth || 280;
      const maxLeft = sectionRect.width - panelWidth - 8;
      if (left > maxLeft) left = Math.max(8, maxLeft);
      if (left < 8) left = 8;

      panel.style.top = `${top}px`;
      panel.style.left = `${left}px`;
    };

    // Background scroll lock for the open bottom-sheet. Event-based on
    // purpose: CSS locks proved destructive in Safari — position: fixed on
    // the body blanked the product images (compositing layers rebuilt),
    // overflow: hidden on the root unstuck the sticky header/filter bar. A
    // touchmove preventDefault changes neither layout nor paint; touches
    // inside the panel are left alone so its options list still scrolls.
    const blockBackgroundTouch = (event) => {
      if (panel.contains(event.target)) {
        // Letting every touch inside the panel through was too generous. When
        // the options list is short enough to fit — which it is with one
        // filter and a handful of categories — there is nothing to scroll, so
        // the browser passes the gesture on to the page and the background
        // scrolls under the open sheet. overscroll-behavior does not help
        // here: it stops scroll CHAINING from a scrollable box, and a box
        // with no overflow never starts a scroll to chain from.
        //
        // So only yield when the panel genuinely has somewhere to go.
        const canScroll = panel.scrollHeight > panel.clientHeight + 1;
        if (canScroll) return;
      }
      event.preventDefault();
    };

    const releaseScrollLock = () => {
      document.removeEventListener('touchmove', blockBackgroundTouch);
      document.body.classList.remove('new-design-featured-products-scroll-lock');
    };

    const closeSheet = () => {
      if (sheet.hidden) return;
      window.clearTimeout(state.closeTimer);

      sheet.classList.remove('is-open');
      panel.style.removeProperty('transform');
      state.dragging = false;
      state.activeButton?.classList.remove('is-open');

      state.closeTimer = window.setTimeout(
        () => {
          sheet.hidden = true;
          sheet.removeAttribute('data-mode');
          panel.style.removeProperty('top');
          panel.style.removeProperty('left');
          state.activeType = '';
          state.activeButton = null;

          // Unlock once the sheet is fully hidden. The overflow lock releases
          // with no visual effect, but keeping it until the exit animation
          // ends means the background can't start scrolling under a sheet
          // that is still sliding away.
          releaseScrollLock();
        },
        isDesktop() ? 160 : 330
      );
    };

    const renderOptions = (type) => {
      options.replaceChildren();
      const optionData =
        type === 'sort'
          ? sortOptions.map(([value, label]) => ({ value, label }))
          : valuesFor(type);

      if (!optionData.length) {
        const empty = document.createElement('li');
        empty.className = 'new-design-featured-products__sheet-option';
        empty.textContent = ((window.sifStrings?.cards?.noOptions) || 'No options available');
        options.appendChild(empty);
        return;
      }

      const allValue = defaultValueFor(type);
      const allData = [{ value: allValue, label: allLabels[type] || ((window.sifStrings?.cards?.all) || 'All'), count: null }];
      const rows = type === 'sort' ? optionData : allData.concat(optionData);

      rows.forEach(({ value, label, count }) => {
        const item = document.createElement('li');
        const optionLabel = document.createElement('label');
        const copy = document.createElement('span');
        const input = document.createElement('input');
        const radio = document.createElement('span');

        optionLabel.className = 'new-design-featured-products__sheet-option';
        copy.className = 'new-design-featured-products__sheet-option-copy';
        input.type = 'radio';
        input.name = `featured-products-${section.dataset.sectionId}-${type}`;
        input.value = value;
        input.checked = state[type] === value;
        radio.className = 'new-design-featured-products__sheet-radio';
        radio.setAttribute('aria-hidden', 'true');

        const optionText = document.createElement('span');
        optionText.textContent = label;
        copy.appendChild(optionText);

        if (typeof count === 'number') {
          const countElement = document.createElement('small');
          countElement.className = 'new-design-featured-products__sheet-option-count';
          countElement.textContent = String(count);
          copy.appendChild(countElement);
        }

        input.addEventListener('change', () => {
          state[type] = value;
          applyState();
          syncReset();
          window.setTimeout(closeSheet, isDesktop() ? 60 : 140);
        });

        optionLabel.append(copy, input, radio);
        item.appendChild(optionLabel);
        options.appendChild(item);
      });

      syncReset();
    };

    const openSheet = (type, button) => {
      // Toggle off if the same desktop popover is already open for this chip.
      if (isDesktop() && !sheet.hidden && state.activeType === type) {
        closeSheet();
        return;
      }

      window.clearTimeout(state.closeTimer);
      state.activeButton?.classList.remove('is-open');
      state.activeType = type;
      state.activeButton = button || buttonFor(type) || null;
      title.textContent = labels[type] || type;
      renderOptions(type);

      sheet.hidden = false;
      sheet.dataset.mode = isDesktop() ? 'popover' : 'sheet';

      if (isDesktop()) {
        panel.style.removeProperty('transform');
        positionPopover();
      }

      // Lock background scroll while the bottom-sheet is open (mobile only).
      // See blockBackgroundTouch for why this is an event lock and not CSS.
      // The body class only hides the chat widget. Re-adding the same
      // listener reference is a no-op, so reopening mid-close is safe.
      if (!isDesktop()) {
        settleLazyImages();
        document.addEventListener('touchmove', blockBackgroundTouch, { passive: false });
        document.body.classList.add('new-design-featured-products-scroll-lock');
      }

      sheet.getBoundingClientRect();
      state.activeButton?.classList.add('is-open');
      window.requestAnimationFrame(() => sheet.classList.add('is-open'));
    };

    const resetActiveFilter = () => {
      if (!state.activeType) return;
      clearFilter(state.activeType);
      // Reset to default and close the sheet (brief delay lets the radio move
      // back to the default option before it slides away).
      window.setTimeout(closeSheet, isDesktop() ? 60 : 140);
    };

    viewButtons.forEach((button) => {
      button.addEventListener('click', () => setView(button.dataset.viewButton));
    });

    filterButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        // A click on the ✕ affordance clears the filter instead of opening.
        if (event.target.closest('[data-filter-clear]')) {
          event.preventDefault();
          clearFilter(button.dataset.filterButton);
          return;
        }
        openSheet(button.dataset.filterButton, button);
      });
    });

    sheet.querySelector('[data-sheet-backdrop]')?.addEventListener('click', closeSheet);
    reset.addEventListener('click', resetActiveFilter);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !sheet.hidden) closeSheet();
    });

    // Desktop: close the popover on any outside click.
    document.addEventListener('click', (event) => {
      if (sheet.hidden || !isDesktop()) return;
      if (panel.contains(event.target)) return;
      if (state.activeButton?.contains(event.target)) return;
      closeSheet();
    });

    // Desktop: keep the popover glued to its chip while scrolling/resizing.
    const reposition = () => {
      if (!sheet.hidden && isDesktop()) positionPopover();
    };
    window.addEventListener('scroll', reposition, { passive: true });
    window.addEventListener('resize', reposition);

    // Swipe-to-close. The grab zone (handle + title header) always starts a
    // drag — it's a big, control-free target that's easy to hit. Elsewhere in
    // the panel a drag only starts when the options list is scrolled to the
    // top, so normal list scrolling still works.
    const startDrag = (event) => {
      if (isDesktop()) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      const fromGrab =
        (grabZone && grabZone.contains(event.target)) || (handle && handle.contains(event.target));
      if (!fromGrab && panel.scrollTop > 0) return;

      state.dragging = true;
      state.dragTracking = true; // potential drag, not yet committed to moving
      state.dragFromGrab = Boolean(fromGrab);
      state.dragStartY = event.clientY;
      state.dragOffset = 0;
    };

    const moveDrag = (event) => {
      if (!state.dragging) return;
      const delta = event.clientY - state.dragStartY;

      // Only downward movement drags the sheet; ignore upward.
      if (delta <= 0) {
        if (!state.dragTracking) {
          state.dragOffset = 0;
          panel.style.transform = '';
        }
        return;
      }

      // Commit to a drag once it's clearly downward (immediately from the grab
      // zone, after a small threshold elsewhere so taps aren't hijacked).
      if (state.dragTracking && (state.dragFromGrab || delta > 6)) {
        state.dragTracking = false;
        panel.style.transition = 'none';
        panel.setPointerCapture?.(event.pointerId);
      }
      if (state.dragTracking) return;

      state.dragOffset = delta;
      panel.style.transform = `translate3d(0, ${delta}px, 0)`;
      event.preventDefault();
    };

    const endDrag = () => {
      if (!state.dragging) return;
      state.dragging = false;
      state.dragTracking = false;
      panel.style.removeProperty('transition');
      if (state.dragOffset > 80) closeSheet();
      else panel.style.removeProperty('transform');
    };

    panel.addEventListener('pointerdown', startDrag);
    panel.addEventListener('pointermove', moveDrag);
    panel.addEventListener('pointerup', endDrag);
    panel.addEventListener('pointercancel', endDrag);

    mobileQuery.addEventListener('change', () => {
      // Layout changed between mobile/desktop — close any open panel and re-flow galleries.
      closeSheet();
      window.requestAnimationFrame(refreshGalleries);
    });

    // Add a bottom divider to the filter bar once it sticks to the top of the
    // viewport (mobile). A sentinel placed just above the bar tells us when the
    // bar has scrolled up to top: 0.
    if (filtersBar && 'IntersectionObserver' in window) {
      const sentinel = document.createElement('div');
      sentinel.className = 'new-design-featured-products__filters-sentinel';
      sentinel.setAttribute('aria-hidden', 'true');
      filtersBar.parentNode.insertBefore(sentinel, filtersBar);

      // On mobile the layout switch lives in the filter row from the start,
      // not just once the bar is pinned. It used to sit up in the heading,
      // which meant scrolling back to the top of the section to change column
      // count — the row is where the thumb already is, and the switch travels
      // with it when the bar sticks.
      //
      // The switch is MOVED, not cloned: two copies would mean two sets of
      // aria-pressed states to keep in sync, and viewButtons was captured at
      // start-up — moving the node keeps those references (and their
      // listeners) live.
      const viewSwitch = section.querySelector('.new-design-featured-products__view-switch');
      const switchHome = viewSwitch?.parentNode;
      const dockSwitch = () => {
        if (!viewSwitch || !switchHome) return;
        // Desktop hides the switch outright; leaving it docked across a resize
        // would strand it in the wrong container.
        const target = isDesktop() ? switchHome : filtersBar;
        if (viewSwitch.parentNode !== target) target.appendChild(viewSwitch);
        viewSwitch.classList.toggle('is-docked', target === filtersBar);
      };
      dockSwitch();

      const stickyObserver = new IntersectionObserver(
        ([entry]) => {
          filtersBar.classList.toggle('is-stuck', entry.intersectionRatio === 0);
        },
        { threshold: [0] }
      );
      stickyObserver.observe(sentinel);

      mobileQuery.addEventListener('change', dockSwitch);
    }

    // Pin each gallery image as soon as it finishes loading, so images that
    // stream in while the user scrolls are protected too — not just the ones
    // already decoded when the sheet is first opened.
    section.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      if (img.complete && img.naturalWidth > 0) {
        img.loading = 'eager';
      } else {
        img.addEventListener('load', () => {
          img.loading = 'eager';
        }, { once: true });
      }
    });

    setView('list');
    applyState();
    initGalleries();
  };

  const initSections = (root = document) => {
    root.querySelectorAll('[data-featured-products-mobile]').forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSections(), { once: true });
  } else {
    initSections();
  }

  document.addEventListener('shopify:section:load', (event) => initSections(event.target));
})();
